import { Box, Card, FlexGrid, StackView, Typography, Badge, Icon, Spacer, StackWrap } from '@telus-uds/components-web'
import Ribbon from '../Ribbon/Ribbon'

import logger from '@/logger/logger'
import { Language } from 'src/siteBuilder/locale'
import { OffersFilterProps, offerCard } from './OffersFilter'
import Image from '../Image/Image'
import { SHOW_ALL_PRODUCTS } from './OffersFilter.reducer'

export const renderOfferCard = (
  card: offerCard,
  hasFilterActive: boolean,
  lang: Language,
  isMobile: boolean,
  equalCardHeight?: boolean
) => {
  // TypeScript ensures card is a valid offerCard object
  const size: { xs: number; md: number; lg: number } | undefined =
    card.fullWidth && !hasFilterActive ? undefined : { xs: 12, md: 6, lg: 4 }

  return (
    <FlexGrid.Col {...size} key={`card-${card.id}`} testID="offer-card-testid" flex={equalCardHeight}>
      <Card
        nativeID={card.anchorID}
        tokens={{
          contentFlexGrow: 1,
          contentJustifyContent: 'space-between',
        }}
        fullBleedContent={
          card.img?.src
            ? {
                position: size !== undefined || isMobile ? 'top' : 'right',
                align: 'center',
                contentAlign: 'center',
                content: (
                  // TODO: test UDS version
                  // <OptimizeImage contentfulAssetUrl={card.img.src} alt={card.img.alt} loading="lazy" />

                  <Image
                    src={card.img.src}
                    alt={card.img.alt ?? ''}
                    width={card.img.width}
                    height={card.img.height}
                    sizes={size !== undefined ? { sm: 100, md: 50, xl: 35 } : undefined}
                  />
                ),
              }
            : undefined
        }
        variant={{ background: card.background, padding: 'narrow' }}
      >
        {!!card.ribbon && <Ribbon left={-8} top={-12} wrap text={card.ribbon} />}

        <StackView space={3}>
          {renderCardBadges(card.campaignBadge, card.tags, lang)}

          {card.heading}

          {card.content}

          {(!!card.cta || !!card.ctaSecondary) && <Spacer space={5} />}
        </StackView>

        {(!!card.cta || !!card.ctaSecondary) && (
          <StackView space={3}>
            {!!card.cta && card.cta}
            {!!card.ctaSecondary && card.ctaSecondary}
          </StackView>
        )}
      </Card>
      <Spacer space={5} />
    </FlexGrid.Col>
  )
}

export const renderCardBadges = (
  campaignBadge: offerCard['campaignBadge'],
  categoryBadge: offerCard['tags'],
  locale: Language
) => {
  const trimmedCampaignBadge = campaignBadge?.trim() ?? ''

  const renderedCampaignBadge = !!trimmedCampaignBadge && (
    <Badge testID="campaign-badge" key={`campaign-badge`} variant={{ alternative: true }}>
      {trimmedCampaignBadge}
    </Badge>
  )

  // TypeScript ensures categoryBadge is TagProps[] if present, but it's optional
  const renderedCategoryBadges =
    categoryBadge &&
    categoryBadge.map((badge, i) => (
      <Badge testID={`category-badge-${badge.id}`} key={`category-badge-${i}`} variant={{ outline: true }}>
        {badge.label}
      </Badge>
    ))

  return (
    <StackWrap direction="row" space={1}>
      {renderedCampaignBadge}
      {renderedCategoryBadges}
    </StackWrap>
  )
}

export const ErrorCard = (icon: React.ElementType, title: string, description: string) => (
  <Card>
    <StackView
      direction="row"
      space={4}
      tokens={{
        alignItems: 'center',
      }}
    >
      <Icon icon={icon} variant={{ size: 'extraLarge', rank: 'primary' }} />
      <Box flex={1}>
        <Typography variant={{ size: 'h2' }}>{title}</Typography>
        <Spacer space={3} />
        <Typography variant={{ color: 'secondary' }}>{description}</Typography>
      </Box>
    </StackView>
  </Card>
)

/**
 *
 * Function to automatically order cards in order to have full width cards display each 5 or 7 cards depending on viewport
 * @param cards offers card to sort
 * @param hasFilterActive whether any filter is active. if no filter active move full width cards to original position
 * @param fullWidthCardPosition check if full width cards should be display each 5 or 7
 *
 * @returns sorted cards
 */
export const orderCards = (
  cards: OffersFilterProps['offerCards'],
  hasFilterActive: boolean,
  fullWidthCardPosition: number
) => {
  // Runtime check for NaN/Infinity - TypeScript can't prevent these
  if (hasFilterActive || fullWidthCardPosition <= 0 || !Number.isFinite(fullWidthCardPosition)) {
    return cards
  }

  // Separate full width from normal cards in a single pass
  const normalCards: typeof cards = []
  const fullWidthCards: typeof cards = []

  for (let i = 0; i < cards.length; i += 1) {
    const card = cards[i]

    if (card.fullWidth) {
      fullWidthCards.push(card)
    } else {
      normalCards.push(card)
    }
  }

  // Build the final array in a single pass - O(n) complexity
  const result: typeof cards = []
  let normalCardIndex = 0
  let fullWidthCardIndex = 0

  while (normalCardIndex < normalCards.length || fullWidthCardIndex < fullWidthCards.length) {
    // Calculate the position where the next full-width card should be inserted
    // Formula: (fullWidthCardIndex + 1) * fullWidthCardPosition + fullWidthCardIndex
    const nextFullWidthPosition =
      fullWidthCardIndex < fullWidthCards.length
        ? (fullWidthCardIndex + 1) * fullWidthCardPosition + fullWidthCardIndex
        : Infinity

    // Add normal cards until we reach the position for the next full-width card
    while (normalCardIndex < normalCards.length && result.length < nextFullWidthPosition) {
      result.push(normalCards[normalCardIndex])
      normalCardIndex += 1
    }

    // Insert the full-width card at the calculated position OR if we can't add more normal cards
    // This prevents infinite loop when there are only full-width cards
    if (
      fullWidthCardIndex < fullWidthCards.length &&
      (result.length === nextFullWidthPosition || normalCardIndex >= normalCards.length)
    ) {
      result.push(fullWidthCards[fullWidthCardIndex])
      fullWidthCardIndex += 1
    }
  }

  return result
}

/**
 * Function to check with how many cards we fill specified limit of rows
 */
export const getCardsToShow = (cards: OffersFilterProps['offerCards'], cols: number) => {
  // Runtime check for NaN/Infinity - TypeScript can't prevent these
  if (!Number.isFinite(cols)) {
    return cards.length
  }

  if (cols === -1) return cards.length + 1

  if (cols < 0) {
    return 0
  }

  let count = 0
  let maxIdx = 0

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i]

    if (count >= cols) break

    maxIdx = i

    if (card.fullWidth) count += 3
    else count += 1
  }

  return maxIdx + 1
}

export const filterCardsByTag = (allCards: OffersFilterProps['offerCards'], urlTags: { id: string }[]) => {
  if (urlTags.length === 0) {
    return allCards
  }

  if (urlTags.some((tag) => tag.id === SHOW_ALL_PRODUCTS)) {
    return allCards
  }

  const filtered = allCards.filter((card) => {
    // Optional tags property - check if it exists before filtering
    if (!card.tags) {
      return false
    }

    return card.tags.some((tag) => urlTags.some((urlTag) => tag.id === urlTag.id))
  })

  if (filtered.length === 0) {
    return []
  }

  return filtered
}

export const getDataFromQueryString = (queryString: string, key: string, defaultValue?: string): string[] => {
  // Runtime validation for external data (URL query strings)
  // TypeScript can't validate runtime string content
  try {
    const params = new URLSearchParams(queryString)
    const data = params.get(key)
    return data ? data.split(',') : defaultValue ? [defaultValue] : []
  } catch (error) {
    logger.error({
      message: 'getDataFromQueryString: Failed to parse query string',
      error: error instanceof Error ? error.message : String(error),
    })
    return defaultValue ? [defaultValue] : []
  }
}
